import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "../components/layout/PageShell";
import DeploymentStatusPanel from "../components/crm/DeploymentStatusPanel";
import ModuleItemsModal from "../components/crm/ModuleItemsModal";
import { getModules } from "../services/crmService";
import toast from "react-hot-toast";

import "./project.css";

const PRIMARY_KEYS = [
    "products",
    "programs",
    "listings",
    "properties",
    "services",
    "treatments",
    "practice-areas",
    "menu-items",
    "features"
];

const SUPPORT_KEYS = [
    "testimonials",
    "faqs",
    "gallery",
    "team",
    "coaches",
    "trainers",
    "doctors",
    "lawyers",
    "pricing",
    "membership",
    "collections",
    "localities"
];

function normalize(value = "") {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/_/g, "-")
        .replace(/\s+/g, "-");
}

function getModuleKey(module) {
    return module?.moduleKey || module?.key || "";
}

function getModuleLabel(module) {
    const key = getModuleKey(module);
    return module?.moduleLabel || module?.label || key || "Module";
}

function getModuleType(module) {
    return module?.moduleType || module?.type || "Content";
}

function getModuleDescription(moduleKey = "", moduleType = "") {
    const key = String(moduleKey).toLowerCase();

    if (key.includes("program")) return "Manage programs, batches, schedules, pricing, and training details.";
    if (key.includes("product")) return "Manage products, prices, images, descriptions, and catalog content.";
    if (key.includes("listing") || key.includes("property")) return "Manage listings, locations, prices, images, and property details.";
    if (key.includes("testimonial")) return "Manage customer reviews and trust-building website content.";
    if (key.includes("team") || key.includes("coach") || key.includes("trainer")) return "Manage team members, coaches, trainers, staff profiles, and expertise.";
    if (key.includes("doctor")) return "Manage doctor profiles, departments, expertise, and appointment-focused content.";
    if (key.includes("lawyer")) return "Manage lawyer profiles, legal expertise, credentials, and consultation content.";
    if (key.includes("practice")) return "Manage legal practice areas, service details, and trust-building legal content.";
    if (key.includes("treatment")) return "Manage treatments, procedures, benefits, and patient-facing healthcare content.";
    if (key.includes("service")) return "Manage services, benefits, descriptions, and website service sections.";
    if (key.includes("faq")) return "Manage common questions shown on your website.";
    if (key.includes("gallery")) return "Manage visual gallery content, images, and showcase sections.";
    if (key.includes("pricing") || key.includes("membership")) return "Manage pricing, memberships, plans, and package content.";
    if (key.includes("collection")) return "Manage ecommerce collections and product grouping shown on the website.";
    if (key.includes("localit")) return "Manage localities, cities, neighborhoods, and real estate discovery content.";
    if (key.includes("feature")) return "Manage product features, SaaS benefits, and conversion-focused feature sections.";

    return `Manage ${moduleType || "website"} content shown inside this module.`;
}

function getModuleIcon(moduleKey = "") {
    const key = String(moduleKey).toLowerCase();

    if (key.includes("program")) return "🎓";
    if (key.includes("product")) return "🛍️";
    if (key.includes("listing") || key.includes("property")) return "🏢";
    if (key.includes("testimonial")) return "⭐";
    if (key.includes("team") || key.includes("coach") || key.includes("trainer")) return "👥";
    if (key.includes("doctor") || key.includes("treatment")) return "🩺";
    if (key.includes("lawyer") || key.includes("practice")) return "⚖️";
    if (key.includes("service")) return "🧩";
    if (key.includes("faq")) return "❓";
    if (key.includes("gallery")) return "🖼️";
    if (key.includes("pricing") || key.includes("membership")) return "💳";
    if (key.includes("collection")) return "🧺";
    if (key.includes("localit")) return "📍";
    if (key.includes("feature")) return "⚡";

    return "📦";
}

function getModuleState(module) {
    if (module?.isEnabled === false) return "Disabled";
    if (module?.isActive === false) return "Inactive";
    return "Enabled";
}

function getModuleCategory(module) {
    const key = normalize(getModuleKey(module));

    if (getModuleState(module) !== "Enabled") return "disabled";
    if (PRIMARY_KEYS.some((x) => key.includes(x))) return "primary";
    if (SUPPORT_KEYS.some((x) => key.includes(x))) return "support";

    return "content";
}

function getModulePriority(module) {
    const key = normalize(getModuleKey(module));
    const state = getModuleState(module);

    if (state !== "Enabled") return 90;

    const primaryIndex = PRIMARY_KEYS.findIndex((x) => key.includes(x));
    if (primaryIndex >= 0) return primaryIndex;

    const supportIndex = SUPPORT_KEYS.findIndex((x) => key.includes(x));
    if (supportIndex >= 0) return 30 + supportIndex;

    return 60;
}

function getModuleActionText(moduleKey = "") {
    const key = String(moduleKey).toLowerCase();

    if (key.includes("product")) return "Manage catalog";
    if (key.includes("program")) return "Manage programs";
    if (key.includes("listing") || key.includes("property")) return "Manage listings";
    if (key.includes("service")) return "Manage services";
    if (key.includes("testimonial")) return "Manage reviews";
    if (key.includes("faq")) return "Manage FAQs";
    if (key.includes("gallery")) return "Manage images";
    if (key.includes("pricing") || key.includes("membership")) return "Manage plans";

    return "Manage items";
}

function ModuleSkeletonGrid() {
    return (
        <div className="modules-page-grid polished">
            {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="module-directory-card skeleton">
                    <div className="module-directory-icon skeleton-box" />
                    <div className="module-directory-copy">
                        <span className="skeleton-line short" />
                        <span className="skeleton-line title" />
                        <span className="skeleton-line" />
                        <span className="skeleton-line wide" />
                    </div>
                    <div className="module-directory-footer">
                        <span className="skeleton-pill" />
                        <span className="skeleton-line short" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function CrmProjectModulesPage() {
    const { projectId } = useParams();

    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [selectedModule, setSelectedModule] = useState(null);

    const load = useCallback(async () => {
        if (!projectId) return;

        try {
            setLoading(true);
            const data = await getModules(projectId);
            setModules(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load project modules.");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        load();
    }, [load]);

    const summary = useMemo(() => {
        const enabled = modules.filter((x) => getModuleState(x) === "Enabled").length;
        const disabled = modules.length - enabled;
        const collection = modules.filter((x) =>
            String(getModuleType(x)).toLowerCase().includes("collection")
        ).length;
        const primary = modules.filter((x) => getModuleCategory(x) === "primary").length;

        return {
            total: modules.length,
            enabled,
            disabled,
            collection,
            primary
        };
    }, [modules]);

    const moduleTypes = useMemo(() => {
        const set = new Set();

        modules.forEach((module) => {
            set.add(getModuleType(module));
        });

        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [modules]);

    const sortedModules = useMemo(() => {
        return [...modules].sort((a, b) => {
            const priorityDiff = getModulePriority(a) - getModulePriority(b);
            if (priorityDiff !== 0) return priorityDiff;

            return getModuleLabel(a).localeCompare(getModuleLabel(b));
        });
    }, [modules]);

    const filteredModules = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        const selectedType = typeFilter.toLowerCase();

        return sortedModules.filter((module) => {
            const moduleKey = getModuleKey(module);
            const moduleLabel = getModuleLabel(module);
            const moduleType = getModuleType(module);
            const description = getModuleDescription(moduleKey, moduleType);
            const state = getModuleState(module);
            const category = getModuleCategory(module);

            const searchable = [
                moduleKey,
                moduleLabel,
                moduleType,
                description,
                state,
                category
            ].join(" ").toLowerCase();

            const matchesSearch = !query || searchable.includes(query);
            const matchesType = selectedType === "all" || moduleType.toLowerCase() === selectedType;
            const matchesCategory = categoryFilter === "all" || category === categoryFilter;

            return matchesSearch && matchesType && matchesCategory;
        });
    }, [sortedModules, searchText, typeFilter, categoryFilter]);

    const primaryModules = useMemo(() => {
        return sortedModules
            .filter((module) => getModuleCategory(module) === "primary")
            .slice(0, 4);
    }, [sortedModules]);

    const resetFilters = () => {
        setSearchText("");
        setTypeFilter("all");
        setCategoryFilter("all");
    };

    const openModuleManager = (module) => {
        setSelectedModule(module);
    };

    return (
        <PageShell title="Project Modules">
            <div className="modules-page-shell">
                <section className="module-hero-premium">
                    <div>
                        <span className="project-kicker">Content Studio</span>
                        <h1>Project Modules</h1>
                        <p>
                            Manage CRM content without leaving this page. Add, edit, improve, publish,
                            and redeploy module items from a fast modal workflow.
                        </p>

                        <div className="module-hero-meta">
                            <span>{summary.total} modules</span>
                            <span>{summary.enabled} enabled</span>
                            <span>{summary.primary} primary content areas</span>
                        </div>
                    </div>

                    <div className="module-hero-actions">
                        <Link className="project-action-btn secondary" to={`/crm/projects/${projectId}`}>
                            Back to Project
                        </Link>

                        <button
                            type="button"
                            className="project-action-btn primary"
                            onClick={load}
                            disabled={loading}
                        >
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                </section>

                <DeploymentStatusPanel
                    projectId={projectId}
                    compact
                    autoRefresh
                    className="modules-deployment-status"
                />

                <section className="module-summary-grid">
                    <div className="module-summary-card">
                        <span>Total Modules</span>
                        <strong>{summary.total}</strong>
                        <small>Available CRM data areas</small>
                    </div>

                    <div className="module-summary-card success">
                        <span>Enabled</span>
                        <strong>{summary.enabled}</strong>
                        <small>Active for content editing</small>
                    </div>

                    <div className="module-summary-card">
                        <span>Collections</span>
                        <strong>{summary.collection}</strong>
                        <small>Repeatable CRM content</small>
                    </div>

                    <div className="module-summary-card muted">
                        <span>Disabled</span>
                        <strong>{summary.disabled}</strong>
                        <small>Hidden or inactive modules</small>
                    </div>
                </section>

                {loading ? (
                    <section className="project-panel">
                        <div className="project-panel-header">
                            <div>
                                <span className="project-kicker">Loading</span>
                                <h2>Preparing module directory...</h2>
                                <p>Fetching project modules and CRM structure.</p>
                            </div>
                        </div>
                        <ModuleSkeletonGrid />
                    </section>
                ) : modules.length === 0 ? (
                    <section className="project-panel module-empty-panel">
                        <span className="project-kicker">No Modules</span>
                        <h2>CRM modules are not ready yet</h2>
                        <p>
                            Generate or repair CRM modules from seed first. Once available, modules such as
                            products, programs, listings, services, testimonials, and FAQs will appear here.
                        </p>

                        <div className="module-empty-actions">
                            <Link className="project-action-btn primary" to={`/crm/projects/${projectId}`}>
                                Back to Project
                            </Link>

                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={load}
                            >
                                Check Again
                            </button>
                        </div>
                    </section>
                ) : (
                    <>
                        {primaryModules.length > 0 && (
                            <section className="project-panel module-priority-panel">
                                <div className="project-panel-header compact">
                                    <div>
                                        <span className="project-kicker">Recommended</span>
                                        <h2>Start with primary content</h2>
                                        <p>
                                            These modules usually control the most important pages of this website.
                                        </p>
                                    </div>
                                </div>

                                <div className="module-quick-grid">
                                    {primaryModules.map((module) => {
                                        const moduleKey = getModuleKey(module);
                                        const moduleLabel = getModuleLabel(module);

                                        return (
                                            <button
                                                key={moduleKey}
                                                type="button"
                                                className="module-quick-card"
                                                onClick={() => openModuleManager(module)}
                                            >
                                                <span>{getModuleIcon(moduleKey)}</span>
                                                <strong>{moduleLabel}</strong>
                                                <small>{getModuleActionText(moduleKey)} →</small>
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        <section className="project-panel">
                            <div className="project-panel-header">
                                <div>
                                    <span className="project-kicker">Module Directory</span>
                                    <h2>Website Content Areas</h2>
                                    <p>
                                        Search, filter, and manage modules. Use the modal manager for faster mobile-friendly editing.
                                    </p>
                                </div>
                            </div>

                            <div className="module-category-tabs">
                                {[
                                    ["all", "All"],
                                    ["primary", "Primary"],
                                    ["content", "CRM Content"],
                                    ["support", "Support"],
                                    ["disabled", "Disabled"]
                                ].map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        className={categoryFilter === value ? "active" : ""}
                                        onClick={() => setCategoryFilter(value)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            <div className="project-filter-bar module-filter-bar">
                                <div className="project-search-box">
                                    <span>Search</span>
                                    <input
                                        value={searchText}
                                        onChange={(event) => setSearchText(event.target.value)}
                                        placeholder="Search modules, type, key, label..."
                                    />
                                </div>

                                <div className="project-filter-group">
                                    <label>
                                        <select
                                            value={typeFilter}
                                            onChange={(event) => setTypeFilter(event.target.value)}
                                        >
                                            <option value="all">All module types</option>
                                            {moduleTypes.map((type) => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </label>

                                    <button
                                        type="button"
                                        className="project-action-btn compact secondary"
                                        onClick={resetFilters}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>

                            <div className="module-results-line">
                                Showing <strong>{filteredModules.length}</strong> of <strong>{modules.length}</strong> modules
                            </div>

                            {filteredModules.length === 0 ? (
                                <div className="project-empty">
                                    No modules match your search or filter.
                                </div>
                            ) : (
                                <div className="modules-page-grid polished">
                                    {filteredModules.map((module) => {
                                        const moduleKey = getModuleKey(module);
                                        const moduleLabel = getModuleLabel(module);
                                        const moduleType = getModuleType(module);
                                        const state = getModuleState(module);
                                        const category = getModuleCategory(module);

                                        return (
                                            <article
                                                key={moduleKey}
                                                className={`module-directory-card ${state.toLowerCase()} category-${category}`}
                                            >
                                                <div className="module-directory-icon">
                                                    {getModuleIcon(moduleKey)}
                                                </div>

                                                <div className="module-directory-copy">
                                                    <span className="project-kicker">{moduleType}</span>
                                                    <h3>{moduleLabel}</h3>
                                                    <p>{getModuleDescription(moduleKey, moduleType)}</p>
                                                </div>

                                                <div className="module-directory-meta">
                                                    <span>Key: {moduleKey}</span>
                                                    <span>{category}</span>
                                                </div>

                                                <div className="module-directory-footer">
                                                    <span className={state === "Enabled" ? "module-state on" : "module-state off"}>
                                                        {state}
                                                    </span>

                                                    <button
                                                        type="button"
                                                        className="module-open-editor-btn"
                                                        onClick={() => openModuleManager(module)}
                                                    >
                                                        {getModuleActionText(moduleKey)} →
                                                    </button>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>

            <ModuleItemsModal
                projectId={projectId}
                module={selectedModule}
                open={!!selectedModule}
                onClose={() => setSelectedModule(null)}
                onChanged={load}
            />
        </PageShell>
    );
}